import { useEffect, useRef, useCallback } from 'react'
import { createChart, CandlestickSeries, createSeriesMarkers } from 'lightweight-charts'
import { getVisibleRangeForTimeframe, getCandleTimeRange } from '../utils/chartUtils'

const ForexChart = ({ 
  data, 
  orders = [], 
  symbol, 
  timeframe, 
  height = 500, 
  allowScroll = true,
  pausedViewportRange = null,
  enableDynamicLoading = false,
  backtestStartTimestamp = null,
  backtestEndTimestamp = null,
  onLoadMoreData = null
}) => {
  const chartContainerRef = useRef(null)
  const chartRef = useRef(null)
  const seriesRef = useRef(null)
  const tooltipRef = useRef(null)
  const showFullChart = false
  const ordersByTimestamp = useRef({})
  const allOrdersChronological = useRef([])
  const candleTimeRanges = useRef({})
  const prevDataLength = useRef(0)
  const savedViewportRange = useRef(null)
  const prevAllowScroll = useRef(allowScroll)
  const loadedRanges = useRef(new Set()) 
  const isLoadingData = useRef(false)
  const pendingLoadRequests = useRef(new Set())

  const get7DayPeriodKey = useCallback((timestamp) => {
    const date = new Date(timestamp * 1000)
    const dayOfWeek = date.getDay()
    const weekStart = new Date(date)
    weekStart.setDate(date.getDate() - dayOfWeek)
    weekStart.setHours(0, 0, 0, 0)
    
    const year = weekStart.getFullYear()
    const month = weekStart.getMonth()
    const day = weekStart.getDate()
    
    return `${year}-${month}-${day}`
  }, [])

  const get7DayPeriodRange = useCallback((timestamp) => {
    const date = new Date(timestamp * 1000)
    const dayOfWeek = date.getDay()
    const weekStart = new Date(date)
    weekStart.setDate(date.getDate() - dayOfWeek)
    weekStart.setHours(0, 0, 0, 0)
    
    const weekEnd = new Date(weekStart)
    weekEnd.setDate(weekStart.getDate() + 6)
    weekEnd.setHours(23, 59, 59, 999)
    
    return {
      start: Math.floor(weekStart.getTime() / 1000),
      end: Math.floor(weekEnd.getTime() / 1000)
    }
  }, [])

  const loadDataForPeriod = useCallback(async (timestamp) => {
    if (!enableDynamicLoading || !onLoadMoreData || !symbol || !timeframe) {
      return
    }

    const periodKey = get7DayPeriodKey(timestamp)
    
    if (loadedRanges.current.has(periodKey) || pendingLoadRequests.current.has(periodKey)) {
      return
    }

    if (backtestStartTimestamp && backtestEndTimestamp) {
      if (timestamp < backtestStartTimestamp || timestamp > backtestEndTimestamp) {
        return
      }
    }

    const periodRange = get7DayPeriodRange(timestamp)
    
    const actualStart = Math.max(periodRange.start, backtestStartTimestamp || periodRange.start)
    const actualEnd = Math.min(periodRange.end, backtestEndTimestamp || periodRange.end)
    
    if (actualStart >= actualEnd) {
      return
    }

    pendingLoadRequests.current.add(periodKey)
    isLoadingData.current = true

    try {
      await onLoadMoreData(symbol, timeframe, actualStart, actualEnd)
      loadedRanges.current.add(periodKey)
    } catch (error) {
      console.error(`Failed to load data for period ${periodKey}:`, error)
    } finally {
      pendingLoadRequests.current.delete(periodKey)
      isLoadingData.current = false
    }
  }, [enableDynamicLoading, onLoadMoreData, symbol, timeframe, backtestStartTimestamp, backtestEndTimestamp, get7DayPeriodKey, get7DayPeriodRange])

  const handleVisibleLogicalRangeChange = useCallback((newVisibleLogicalRange) => {
    if (!enableDynamicLoading || !data || data.length === 0 || !newVisibleLogicalRange) {
      return
    }

    const { from, to } = newVisibleLogicalRange
    
    const bufferThreshold = 20 
    const dataLength = data.length
    
    if (from <= bufferThreshold && data[0]) {
      const earliestTimestamp = data[0].time
      const sevenDaysEarlier = earliestTimestamp - (7 * 24 * 60 * 60)
      loadDataForPeriod(sevenDaysEarlier)
    }
    
    if (to >= dataLength - bufferThreshold && data[dataLength - 1]) {
      const latestTimestamp = data[dataLength - 1].time
      const sevenDaysLater = latestTimestamp + (7 * 24 * 60 * 60)
      loadDataForPeriod(sevenDaysLater)
    }
  }, [enableDynamicLoading, data, loadDataForPeriod])

  useEffect(() => {
    const ordersMap = {}
    const ordersArray = []

    if (orders && orders.length > 0) {
      orders.forEach(order => {
        const timestamp = typeof order.date === 'number' ?
          order.date : Math.floor(new Date(order.date).getTime() / 1000)

        const orderWithTimestamp = {
          ...order,
          normalizedTimestamp: timestamp
        }

        ordersArray.push(orderWithTimestamp)

        if (!ordersMap[timestamp]) {
          ordersMap[timestamp] = []
        }
        ordersMap[timestamp].push(orderWithTimestamp)
      })

      ordersArray.sort((a, b) => a.normalizedTimestamp - b.normalizedTimestamp)
    }

    ordersByTimestamp.current = ordersMap
    allOrdersChronological.current = ordersArray

    if (data && data.length > 0) {
      const timeRangeMap = {}
      data.forEach(candle => {
        const timestamp = candle.time
        timeRangeMap[timestamp] = getCandleTimeRange(timestamp, timeframe)
      })
      candleTimeRanges.current = timeRangeMap
    }

  }, [orders, data, timeframe])

  useEffect(() => {
    if (chartRef.current && prevAllowScroll.current === false && allowScroll === true) {
      try {
        const currentRange = chartRef.current.timeScale().getVisibleLogicalRange()
        if (currentRange) {
          savedViewportRange.current = currentRange
        }
      } catch (error) {
        console.error('Error saving viewport range:', error)
      }
    }
    prevAllowScroll.current = allowScroll
  }, [allowScroll])

  useEffect(() => {
    if (chartRef.current && seriesRef.current && data && data.length > 0) {
      const currentDataLength = data.length
      const previousLength = prevDataLength.current

      if (currentDataLength > previousLength && !allowScroll && previousLength > 0) {
        const visibleCandleCount = getVisibleRangeForTimeframe(timeframe, currentDataLength).to - 
                                   getVisibleRangeForTimeframe(timeframe, currentDataLength).from + 1

        const newTo = currentDataLength - 1
        const newFrom = Math.max(0, newTo - visibleCandleCount + 1)

        const newVisibleRange = {
          from: newFrom,
          to: newTo
        }

        chartRef.current.timeScale().setVisibleLogicalRange(newVisibleRange)
      }

      prevDataLength.current = currentDataLength
    }
  }, [data, allowScroll, timeframe])

  useEffect(() => {
    if (chartRef.current && seriesRef.current && data && data.length > 0) {
      seriesRef.current.setData(data)
    }
  }, [data])

  useEffect(() => {
    if (chartRef.current) {
      try {
        chartRef.current.applyOptions({
          timeScale: {
            timeVisible: true,
            secondsVisible: false,
            borderColor: '#dcdee0',
            handleScroll: allowScroll,
            handleScale: allowScroll,
            fixLeftEdge: true,
            fixRightEdge: true,
          }
        })

        if (allowScroll && savedViewportRange.current) {
          chartRef.current.timeScale().setVisibleLogicalRange(savedViewportRange.current)
          savedViewportRange.current = null 
        }
      } catch (error) {
        console.error('Error updating chart options:', error)
      }
    }
  }, [allowScroll])

  useEffect(() => {
    if (chartRef.current && pausedViewportRange && allowScroll) {
      try {
        chartRef.current.timeScale().setVisibleLogicalRange(pausedViewportRange)
      } catch (error) {
        console.error('Error setting paused viewport range:', error)
      }
    }
  }, [pausedViewportRange, allowScroll])
  
  useEffect(() => {
    if (chartContainerRef.current) {
      if (chartRef.current) {
        chartRef.current.remove()
        chartRef.current = null
        seriesRef.current = null
      }

      chartContainerRef.current.style.position = 'relative'

      if (!tooltipRef.current) {
        tooltipRef.current = document.createElement('div')
        tooltipRef.current.className = 'order-tooltip'
        tooltipRef.current.style.display = 'none'
        tooltipRef.current.style.position = 'absolute'
        tooltipRef.current.style.padding = '8px'
        tooltipRef.current.style.backgroundColor = 'rgba(255, 255, 255, 0.9)'
        tooltipRef.current.style.border = '1px solid #ddd'
        tooltipRef.current.style.borderRadius = '4px'
        tooltipRef.current.style.boxShadow = '0 2px 5px rgba(0,0,0,0.1)'
        tooltipRef.current.style.zIndex = '3'
        tooltipRef.current.style.fontSize = '12px'
        tooltipRef.current.style.maxWidth = '250px'
        tooltipRef.current.style.pointerEvents = 'none'
        chartContainerRef.current.appendChild(tooltipRef.current)
      } else {
        tooltipRef.current.style.display = 'none'
        tooltipRef.current.innerHTML = ''
      }

      try {
        const chart = createChart(chartContainerRef.current, {
          height: height,
          layout: {
            background: { type: 'solid', color: '#ffffff' },
            textColor: '#333',
          },
          grid: {
            vertLines: { color: '#f0f0f0' },
            horzLines: { color: '#f0f0f0' },
          },
          handleScale: {
            axisDoubleClickReset: false,
            mouseWheel: false,
            pinch: false,
          },
          crosshair: {
            mode: 1,
            vertLine: {
              color: 'rgba(70, 130, 180, 0.5)',
              width: 1,
              style: 2,
              labelVisible: true,
            },
            horzLine: {
              color: 'rgba(70, 130, 180, 0.5)',
              width: 1,
              style: 2,
              labelVisible: true,
            }
          },
          timeScale: {
            timeVisible: true,
            secondsVisible: false,
            borderColor: '#dcdee0',
            handleScroll: allowScroll,
            handleScale: allowScroll,
            fixLeftEdge: true,
            fixRightEdge: true,
          },
          rightPriceScale: {
            borderColor: '#dcdee0',
            scaleMargins: {
              top: 0.1,
              bottom: 0.1,
            },
          },
          watermark: {
            visible: true,
            text: `${symbol} - ${timeframe}`,
            color: 'rgba(0, 0, 0, 0.1)',
            fontSize: 40,
          }
        })

        const series = chart.addSeries(CandlestickSeries, {
          upColor: '#26a69a',
          downColor: '#ef5350',
          borderVisible: false,
          wickUpColor: '#26a69a',
          wickDownColor: '#ef5350',
        })

        chartRef.current = chart
        seriesRef.current = series

        if (enableDynamicLoading) {
          chart.timeScale().subscribeVisibleLogicalRangeChange(handleVisibleLogicalRangeChange)
        }

        if (data && data.length > 0) {
          series.setData(data)

          chart.subscribeCrosshairMove(param => {
            if (
              param === undefined ||
              param.time === undefined ||
              param.point === undefined ||
              param.point.x < 0 ||
              param.point.y < 0
            ) {
              if (tooltipRef.current) {
                tooltipRef.current.style.display = 'none'
              }
              return
            }
            const timestamp = param.time

            let ordersAtThisCandle = []

            const timeRange = candleTimeRanges.current[timestamp] || getCandleTimeRange(timestamp, timeframe)

            ordersAtThisCandle = allOrdersChronological.current.filter(order => {
              if (!order || typeof order.normalizedTimestamp !== 'number') return false

              return (
                order.normalizedTimestamp >= timeRange.start &&
                order.normalizedTimestamp <= timeRange.end
              )
            })

            if (ordersAtThisCandle.length === 0) {
              if (tooltipRef.current) {
                tooltipRef.current.style.display = 'none'
              }
              return
            }

            updateTooltip(timestamp, param.point, ordersAtThisCandle, timeframe)
          })

          const updateTooltip = (timestamp, point, ordersAtThisCandle, timeframe) => {
            if (!tooltipRef.current) return

            const timeRange = candleTimeRanges.current[timestamp] || getCandleTimeRange(timestamp, timeframe)
            const startDate = new Date(timeRange.start * 1000)
            const endDate = new Date(timeRange.end * 1000)

            let timeRangeDisplay = ''
            const tf = timeframe ? timeframe.toLowerCase() : 'm1'

            if (tf === 'd1' || tf === 'w1' || tf === 'mn1') {
              const startFormatted = startDate.toLocaleDateString()
              const endFormatted = endDate.toLocaleDateString()
              timeRangeDisplay = startFormatted === endFormatted ?
                startFormatted :
                `${startFormatted} - ${endFormatted}`
            } else {
              if (startDate.getDate() === endDate.getDate() &&
                startDate.getMonth() === endDate.getMonth() &&
                startDate.getFullYear() === endDate.getFullYear()) {
                timeRangeDisplay = `${startDate.toLocaleDateString()} ${startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
              } else {
                timeRangeDisplay = `${startDate.toLocaleDateString()} ${startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${endDate.toLocaleDateString()} ${endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
              }
            }

            let tooltipContent = `<div style="font-weight:bold;margin-bottom:5px;border-bottom:1px solid #ddd;padding-bottom:3px;">
              Orders for ${timeRangeDisplay} (${timeframe})
            </div>`

            const sortedOrders = [...ordersAtThisCandle].sort((a, b) => a.normalizedTimestamp - b.normalizedTimestamp)

            sortedOrders.forEach(order => {
              const isBuy = order.type.toLowerCase() === 'buy'
              const orderColor = isBuy ? '#2196F3' : '#FF5252'
              const orderSymbol = isBuy ? '▲' : '▼'
              const orderTime = new Date(order.normalizedTimestamp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

              tooltipContent += `
                <div style="display:flex;justify-content:space-between;margin-bottom:4px;align-items:center;gap:8px">
                  <span>
                    <span style="color:${orderColor};margin-right:5px;">${orderSymbol} ${order.type.toUpperCase()}</span> <small style="opacity:0.7">${orderTime}</small>
                  </span>
                  <span><small style="opacity:0.7">#${order.orderId} @ </small>${order.price}</span>
                </div>
                `
            })

            tooltipRef.current.innerHTML = tooltipContent
            tooltipRef.current.style.display = 'block'

            if (point) {
              const chartRect = chartContainerRef.current.getBoundingClientRect()
              const tooltipWidth = tooltipRef.current.offsetWidth

              let left = point.x + 20
              if (left + tooltipWidth > chartRect.width) {
                left = point.x - tooltipWidth - 10
              }

              tooltipRef.current.style.left = `${left}px`
              tooltipRef.current.style.top = `${point.y - 25}px`
            }
          }

          if (orders && orders.length > 0) {

            const ordersByCandle = {}

            const findAppropriateCandle = (orderTimestamp) => {
              if (!data || data.length === 0) return orderTimestamp

              try {
                const exactMatch = data.find(candle => candle.time === orderTimestamp)
                if (exactMatch) return orderTimestamp

                const timeRange = getCandleTimeRange(orderTimestamp, timeframe)

                const matchingCandle = data.find(candle =>
                  candle.time >= timeRange.start && candle.time <= timeRange.end
                )

                if (matchingCandle) return matchingCandle.time

                let closestCandle = data[0]
                let minDistance = Math.abs(closestCandle.time - orderTimestamp)

                data.forEach(candle => {
                  const distance = Math.abs(candle.time - orderTimestamp)
                  if (distance < minDistance) {
                    closestCandle = candle
                    minDistance = distance
                  }
                })

                return closestCandle?.time || orderTimestamp
              } catch (error) {
                console.error('Error finding appropriate candle:', error)
                return orderTimestamp
              }
            }

            orders.forEach(order => {
              const timestamp = typeof order.date === 'number' ?
                order.date :
                Math.floor(new Date(order.date).getTime() / 1000)

              order.normalizedTimestamp = timestamp

              const timeRange = getCandleTimeRange(timestamp, timeframe)
              order.candleTimeRange = timeRange

              const candleTime = findAppropriateCandle(timestamp)
              const orderType = order.type.toLowerCase()

              const key = `${candleTime}:${orderType}`

              if (!ordersByCandle[key]) {
                ordersByCandle[key] = {
                  candle: candleTime,
                  type: orderType,
                  orders: []
                }
              }

              ordersByCandle[key].orders.push(order)
            })

            const markers = Object.values(ordersByCandle).map(group => {
              const isBuy = group.type === 'buy'
              const orderCount = group.orders.length

              return {
                time: group.candle,
                position: isBuy ? 'belowBar' : 'aboveBar',
                color: isBuy ? '#2196F3' : '#FF5252',
                shape: isBuy ? 'arrowUp' : 'arrowDown',
                text: orderCount > 1 ? `${orderCount}` : '',
                size: 1,
              }
            })

            createSeriesMarkers(series, markers)
          }

          let visibleLogicalRange = getVisibleRangeForTimeframe(timeframe, data.length)

          if (orders && orders.length > 0) {
            const sortedOrders = [...orders].sort((a, b) => {
              const timestampA = typeof a.date === 'number' ? a.date : Math.floor(new Date(a.date).getTime() / 1000)
              const timestampB = typeof b.date === 'number' ? b.date : Math.floor(new Date(b.date).getTime() / 1000)
              return timestampA - timestampB
            })

            const orderTimestamps = sortedOrders.map(order => {
              const timestamp = typeof order.date === 'number' ?
                order.date : Math.floor(new Date(order.date).getTime() / 1000)
              return timestamp
            })

            const orderIndexes = orderTimestamps.map(timestamp => {
              return data.findIndex(candle => {
                return candle.time === timestamp
              })
            }).filter(index => index !== -1)

            if (orderIndexes.length > 0) {
              const min = Math.min(...orderIndexes)

              const firstVisibleOrder = orderIndexes.find(index =>
                index >= visibleLogicalRange.from && index <= visibleLogicalRange.to
              )

              if (!firstVisibleOrder || min < visibleLogicalRange.from) {
                const padding = Math.min(20, Math.floor(visibleLogicalRange.to - visibleLogicalRange.from) / 4)
                const newFrom = Math.max(0, min - padding)
                const visibleCandleCount = visibleLogicalRange.to - visibleLogicalRange.from
                const newTo = Math.min(data.length - 1, newFrom + visibleCandleCount)

                visibleLogicalRange = { from: newFrom, to: newTo }
              }
            }
          }

          if (showFullChart) {
            chart.timeScale().fitContent()
          } else {
            if (allowScroll) {
              const rangeToUse = pausedViewportRange || visibleLogicalRange
              chart.timeScale().setVisibleLogicalRange(rangeToUse)
            }
          }

          if (enableDynamicLoading && data.length > 0) {
            const rangeForDynamicLoading = pausedViewportRange || visibleLogicalRange
            handleVisibleLogicalRangeChange(rangeForDynamicLoading)
          }
        }

        const handleResize = () => {
          if (chartContainerRef.current && chartRef.current) {
            chart.resize(
              chartContainerRef.current.clientWidth,
              chart.options().height
            )
          }
        }

        const handleWheel = (event) => {
          if (event.ctrlKey) {
            event.preventDefault()
          }
        }

        window.addEventListener('resize', handleResize)

        const currentContainer = chartContainerRef.current
        currentContainer.addEventListener('wheel', handleWheel, { passive: false })

        handleResize()

        return () => {
          window.removeEventListener('resize', handleResize)
          if (currentContainer) {
            currentContainer.removeEventListener('wheel', handleWheel)
          }
          if (chartRef.current) {
            chartRef.current.remove()
            chartRef.current = null
            seriesRef.current = null
          }
          if (tooltipRef.current && tooltipRef.current.parentNode) {
            tooltipRef.current.parentNode.removeChild(tooltipRef.current)
            tooltipRef.current = null
          }
        }
      } catch (error) {
        console.error('Error creating chart:', error)
      }
    }
  }, [height, symbol, timeframe, orders, showFullChart, enableDynamicLoading, handleVisibleLogicalRangeChange, pausedViewportRange])

  return (
    <div style={{ position: 'relative' }}>
      <div ref={chartContainerRef} style={{ width: '100%' }} />
    </div>
  )
}

export default ForexChart
